import { useState } from "react";
import { Maximize2, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Reveal } from "./Reveal";

const CHART_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_sabbath-way/artifacts/eizo319c_ChatGPT%20Image%20Aug%2021%2C%202026%2C%2011_43_53%20PM.png";

const paragraphs = [
  "Apostle Paul was indeed commissioned as an apostle to the Gentiles, but he was neither the founder nor the creator of Christianity. His ministry did not introduce a new religion, establish a new system of doctrine, or replace the teachings previously given by Christ and the other apostles.",
  "Rather, Paul consistently presented himself as a servant and messenger of the same Gospel that had already been proclaimed. His teachings were rooted in the Scriptures and centered on the Messiah, the promises of God, and the faith delivered through the apostles. Paul did not preach a contradictory doctrine; he carried the same message to the Gentiles, explaining how they could be brought into the covenant promises of God through faith in Christ.",
  "Therefore, Paul should be understood not as the founder of Christianity, but as an apostle entrusted with extending the already-established Apostolic Gospel to the nations. His ministry represents continuity\u2014not contradiction\u2014with the teachings of Christ and the apostles who preceded him.",
];

export const Paul = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="paul" data-testid="paul-section" className="bg-[#141311] py-24 md:py-32 border-y border-[#2B2825]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
              Continuity, Not Contradiction
            </p>
            <h2 className="font-heading font-light tracking-tight text-4xl md:text-6xl leading-tight mb-8">
              The Apostle <span className="italic text-[#D4AF37]">Paul</span>
            </h2>
          </Reveal>
          <div className="space-y-5">
            {paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="text-base md:text-lg leading-relaxed font-light text-[#A39E98]">
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <blockquote className="mt-8 border-l-2 border-[#D4AF37] pl-6 font-heading italic text-xl md:text-2xl font-light text-[#F4F0EA]/90">
              "Follow my example, just as I follow the example of Christ."
              <cite className="mt-2 block font-mono text-xs not-italic tracking-wide text-[#D4AF37]">
                — 1 Corinthians 11:1
              </cite>
            </blockquote>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <button
            data-testid="paul-chart-trigger"
            onClick={() => setOpen(true)}
            className="group relative block w-full overflow-hidden border border-[#2B2825]"
          >
            <img
              src={CHART_IMG}
              alt="Christ & Paul: Same Sabbath, Same Feasts, Same Law"
              className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-[#0A0A0A]/0 group-hover:bg-[#0A0A0A]/20 transition-colors" />
            <span className="absolute bottom-4 right-4 flex items-center gap-2 border border-[#D4AF37] bg-[#0A0A0A]/70 backdrop-blur-sm text-[#D4AF37] px-4 py-2 text-xs tracking-wide uppercase font-mono opacity-90 group-hover:opacity-100 transition-opacity">
              <Maximize2 strokeWidth={1.5} className="h-4 w-4" />
              Expand
            </span>
          </button>
        </Reveal>
      </div>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            data-testid="paul-chart-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-10 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
          >
            <DialogPrimitive.Title className="sr-only">Christ & Paul: Same Sabbath, Same Feasts, Same Law</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">
              A full-size comparison chart of the practices and teachings of Christ and the Apostle Paul.
            </DialogPrimitive.Description>
            <DialogPrimitive.Close
              data-testid="paul-chart-close"
              className="fixed top-6 right-6 z-10 flex items-center gap-2 text-[#A39E98] hover:text-[#F4F0EA] transition-colors"
            >
              <span className="text-sm tracking-wide">Close</span>
              <X strokeWidth={1} className="h-5 w-5" />
            </DialogPrimitive.Close>
            <img
              src={CHART_IMG}
              alt="Christ & Paul: Same Sabbath, Same Feasts, Same Law"
              className="max-h-full max-w-full object-contain border border-[#4A453F]"
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </section>
  );
};
